import React from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { FiArrowRight, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const About = () => {
  const { settings } = useApp();

  return (
    <div className="bg-brand-light">
      {/* About Hero */}
      <section className="bg-brand-dark text-white py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover" alt="Sky" />
        </div>
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-serif font-bold mb-6"
          >
            {settings.about.heroTitle}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="text-xl text-brand-gold mb-4"
          >
            {settings.about.heroSubtitle}
          </motion.p>
          <motion.p 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.2 }}
            className="text-lg text-white/70 max-w-2xl mx-auto"
          >
            {settings.about.heroDescription}
          </motion.p>
        </div>
      </section>

      {/* Main Copy */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto prose prose-lg prose-brand">
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p className="text-2xl font-serif text-brand-green leading-snug">
              Travel means different things to different people.
            </p>
            <p>
              For some, it means finally taking the trip they have been putting off for years. For others, it means learning how to travel smarter, more comfortably, or more independently. And for some, it becomes something more — a new interest, a new skill, or even the beginning of a travel-related business path.
            </p>
            <p>
              Golden Compass Travel Guides was built to support all of those starting points. This brand exists to make travel easier to understand and easier to explore.
            </p>
            <p>
              My approach is practical, encouraging, and designed to reduce confusion. I believe travel content should help people move forward — not overwhelm them.
            </p>
          </div>
        </div>
      </section>

      {/* Why I Created This */}
      <section className="py-20 px-4 bg-brand-light">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-serif font-bold text-brand-dark mb-8 text-center">Why I Created Golden Compass Travel Guides</h2>
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-brand-gold/20">
            <p className="text-lg text-gray-700 mb-6">
              I created Golden Compass Travel Guides because travel is often filled with two problems at the same time: <strong>too much noise and not enough guidance.</strong>
            </p>
            <p className="text-lg text-gray-700">
              People want useful information. They want ideas they can trust. They want to know where to start, what fits them best, and what next step makes sense. Too often, they get scattered information instead of clear direction.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {["Finding the right travel guide", "Exploring a specific travel style", "Learning about a destination", "Discovering travel as a business"].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-brand-green font-medium">
                  <SafeIcon icon={FiCheck} /> {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brand Voice / Promise */}
      <section className="py-20 px-4 bg-brand-green text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-serif font-bold mb-12 text-brand-gold">What You Can Expect From Me</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {["Clear", "Helpful", "Practical", "Encouraging", "Focused"].map((word, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center mb-3">
                  <SafeIcon icon={FiCheck} className="text-brand-gold" />
                </div>
                <span className="font-serif text-sm tracking-widest uppercase">{word}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-24 px-4 bg-white text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-serif font-bold mb-6 text-brand-dark">Start With the Right Next Step</h2>
          <p className="text-gray-600 mb-10">If you’re new here, the best place to begin is the Start Here page.</p>
          <a 
            href="/#/" 
            className="inline-flex items-center gap-2 bg-brand-green text-white px-10 py-4 rounded-full font-bold hover:bg-brand-dark transition-all transform hover:scale-105 shadow-xl"
          >
            Visit the Hub <SafeIcon icon={FiArrowRight} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;